import { computed, makeObservable } from 'mobx';

import LazyLoadingKeyValueStore, {
  EventBasedTransport,
} from '@codefluegel/lazy-loading-key-value-store';
import {
  MaexchenCollectCredit,
  MaexchenUserData,
  MaexchenUserProfileData,
  MaexchenRedeemCredit,
  UserData,
} from '../types/transport';
import PointsStore, { OrderElement } from './PointsStore';

export interface ProfileData extends MaexchenUserProfileData {
  userId: string;
  pointsStore: PointsStore;
}

interface MaexchenUserDataWithPoints extends MaexchenUserData {
  pointsStore: PointsStore;
}

export interface OrderElementWithUser extends OrderElement {
  firstname: string;
  lastname: string;
  address: string;
}

export default class UserStore extends LazyLoadingKeyValueStore<
  MaexchenUserData,
  MaexchenUserDataWithPoints
> {
  constructor(
    transport: EventBasedTransport<MaexchenUserData>,
    private collectedTransportFactory: (
      groupId: string,
    ) => EventBasedTransport<MaexchenCollectCredit>,
    private redeemedTransportFactory: (
      groupId: string,
    ) => EventBasedTransport<MaexchenRedeemCredit>,
    private getUserDataTransport: (uid: string) => Promise<UserData>,
  ) {
    super(transport, (userData, key, _, oldEntry) => ({
      ...userData,
      pointsStore: oldEntry
        ? oldEntry.pointsStore
        : this.createPointsStore(key),
    }));
    makeObservable(this, {
      userProfiles: computed,
      allUserOrders: computed,
      allEntries: computed,
    });
  }

  private createPointsStore(groupID: string) {
    return new PointsStore(
      this.collectedTransportFactory(groupID),
      this.redeemedTransportFactory(groupID),
      this.getUserDataTransport,
    );
  }

  get userProfiles(): ProfileData[] {
    return Object.entries(this.entries).map(([key, values]) => ({
      ...values.profile,
      userId: key,
      shareToken: values.shareToken,
      pointsStore: values.pointsStore,
    }));
  }

  get allUserOrders(): OrderElementWithUser[] {
    return this.userProfiles.flatMap(user => {
      const { allOrders } = user.pointsStore;

      // adding firstname and lastname to each order to display in table
      const { firstName, lastName } = user;

      const address = `${user.addressStreet} ${user.addressNumber} ${user.addressZip} ${user.addressCity}`;
      const company = `${user.companyUid} ${user.companyName}`;

      // typed as OrderElementWithUser to add firstname and lastname
      return allOrders.map(order =>
        // needed for export to csv
        ({
          ...order,
          firstname: firstName,
          lastname: lastName,
          address,
          company,
        }),
      );
    });
  }

  get allEntries(): MaexchenUserData[] {
    return this.entriesArray;
  }

  getSearchResults(text: string): ProfileData[] {
    const lowercasedSearchText = text.toLowerCase().trim();

    const filtered = this.userProfiles.filter(
      val =>
        val.firstName.toLowerCase().includes(lowercasedSearchText) ||
        val.lastName.toLowerCase().includes(lowercasedSearchText),
    );

    return text ? filtered : this.userProfiles;
  }
}
